import type { Cart, Order, Product, Address, ApiError } from './types'

export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://api.ensemble.fyi'

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(API_BASE + path, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })
  if (res.status === 204) return undefined as T
  const text = await res.text()
  let data: unknown = null
  try { data = text ? JSON.parse(text) : null } catch { data = null }
  if (!res.ok) {
    const e = (data ?? {}) as Partial<ApiError>
    const err: ApiError = { error: e.error ?? res.statusText, code: e.code, status: res.status }
    throw err
  }
  return data as T
}

export const api = {
  products: (q: Record<string, string> = {}) =>
    request<{ products: Product[]; count: number }>('GET', '/api/products?' + new URLSearchParams(q).toString()),
  product: (id: string) => request<Product>('GET', `/api/products/${encodeURIComponent(id)}`),
  related: (id: string) => request<{ products: Product[] }>('GET', `/api/products/${encodeURIComponent(id)}/related`),
  createCart: () => request<Cart>('POST', '/api/carts', { source: 'web' }),
  cart: (id: string) => request<Cart>('GET', `/api/carts/${id}`),
  addItem: (id: string, sku: string, color: string, size: string, qty = 1) =>
    request<Cart>('POST', `/api/carts/${id}/items`, { sku, color, size, qty }),
  updateItem: (id: string, line: string, qty: number) => request<Cart>('PATCH', `/api/carts/${id}/items/${line}`, { qty }),
  removeItem: (id: string, line: string) => request<Cart>('DELETE', `/api/carts/${id}/items/${line}`),
  promo: (id: string, code: string) => request<Cart>('POST', `/api/carts/${id}/promo`, { code }),
  checkout: (payload: {
    cart_id: string; email: string; address: Address; shipping_method: string
    card: { number: string; expiry: string; cvc: string; name: string }; source?: string
  }) => request<Order>('POST', '/api/checkout', payload),
  order: (id: string) => request<Order>('GET', `/api/orders/${id}`),
  pageview: (page: string) => request<void>('POST', '/api/pageview', { page }).catch(() => undefined),
  promoClick: (campaign: string) => request<void>('POST', '/api/promo/click', { campaign }).catch(() => undefined),
}

export const money = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
